import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import {
  getPts2Context,
  getPts2CurrentStatus,
  requestPts2CurrentStatusRefresh,
} from '../api.js'
import { getPts2History } from './history.js'
import { useI18n } from '../i18n.js'
import {
  choosePts2Controller as savePts2Controller,
  loadPts2Context,
  session,
} from '../session.js'

const terminalAlertStates = new Set(['ENDED', 'FINISHED', 'RESOLVED', 'CLEARED', 'INACTIVE'])

function numberOrNull(value) {
  if (value === '' || value == null) return null
  const result = Number(value)
  return Number.isFinite(result) ? result : null
}

function textOrNull(value) {
  const result = String(value ?? '').trim()
  return result || null
}

function firstNumber(...values) {
  for (const value of values) {
    const parsed = numberOrNull(value)
    if (parsed != null) return parsed
  }
  return null
}

function at(value, index) {
  return Array.isArray(value) ? value[index] : value
}

function clampPercent(value) {
  const parsed = numberOrNull(value)
  return parsed == null ? null : Math.max(0, Math.min(100, parsed))
}

export function normalizePts2Context(value = {}) {
  const controllers = Array.isArray(value.controllers) ? value.controllers.filter((item) => item?.id) : []
  const requested = numberOrNull(value.activeControllerId)
  const requestedAvailable = controllers.some((item) => Number(item.id) === requested)
  const activeControllerId = requestedAvailable ? requested : (controllers.length === 1 ? Number(controllers[0].id) : null)
  return {
    stationId: numberOrNull(value.stationId),
    activeControllerId,
    selectionRequired: Boolean(value.selectionRequired || (!activeControllerId && controllers.length > 1)),
    controllers,
  }
}

export function pts2FreshnessView(value) {
  const freshness = String(value ?? '').toUpperCase()
  if (freshness === 'FRESH' || freshness === 'CONNECTED') return { key: 'fresh', label: 'Онлайн' }
  if (freshness === 'STALE') return { key: 'stale', label: 'Дані застаріли' }
  if (freshness === 'OFFLINE' || freshness === 'DISCONNECTED') return { key: 'offline', label: 'Немає зв’язку' }
  return { key: 'unknown', label: 'Стан невідомий' }
}

function transportLabel(source) {
  const value = String(source ?? '').toUpperCase()
  if (value.includes('WEBSOCKET') || value === 'WSS') return 'WebSocket'
  if (value.includes('HTTPS')) return 'HTTPS'
  if (value === 'UPLOAD_STATUS') return 'UploadStatus'
  return textOrNull(source)
}

function pumpState(value) {
  const normalized = String(value ?? '').replace(/Status$/i, '').toUpperCase()
  if (['FILLING', 'DELIVERY', 'DISPENSING', 'AUTHORIZED', 'STARTED', 'BUSY'].includes(normalized)) return 'filling'
  if (['ERROR', 'OFFLINE', 'UNAVAILABLE', 'BLOCKED'].includes(normalized)) return 'error'
  return 'idle'
}

function normalizePumpItem(item, stateValue, index = 0) {
  const id = firstNumber(item?.Id, item?.Pump, item?.Number, item?.PumpId, item)
  if (id == null) return null
  return {
    id,
    state: pumpState(item?.State ?? item?.Status ?? stateValue),
    nozzle: firstNumber(item?.Nozzle, item?.NozzleId),
    fuelGradeName: textOrNull(item?.FuelGradeName ?? item?.ProductName ?? item?.FuelGrade),
    price: firstNumber(item?.Price, item?.UnitPrice),
    volume: firstNumber(item?.Volume, item?.CurrentVolume),
    amount: firstNumber(item?.Amount, item?.CurrentAmount),
    progressPercent: clampPercent(item?.ProgressPercent ?? item?.ProgressPercentage ?? item?.Percent),
    sourceIndex: index,
  }
}

function statusPumps(status) {
  const source = status?.Pumps
  if (Array.isArray(source)) return source.map((item, index) => normalizePumpItem(item, item?.State, index)).filter(Boolean)
  if (Array.isArray(source?.Items)) return source.Items.map((item, index) => normalizePumpItem(item, item?.State, index)).filter(Boolean)
  if (!source || typeof source !== 'object') return []

  const result = []
  for (const [groupName, group] of Object.entries(source)) {
    if (!group || typeof group !== 'object') continue
    const ids = Array.isArray(group.Ids) ? group.Ids : (Array.isArray(group.Pumps) ? group.Pumps : [])
    ids.forEach((id, index) => {
      const item = normalizePumpItem({
        Id: id,
        Nozzle: at(group.Nozzles, index) ?? at(group.NozzleIds, index) ?? at(group.NozzlesUp, index),
        FuelGradeName: at(group.FuelGradeNames, index) ?? at(group.FuelGrades, index) ?? at(group.FuelGradeName, index),
        Price: at(group.Prices, index) ?? at(group.Price, index),
        Volume: at(group.Volumes, index) ?? at(group.Volume, index),
        Amount: at(group.Amounts, index) ?? at(group.Amount, index),
        ProgressPercent: at(group.ProgressPercentages, index) ?? at(group.ProgressPercent, index),
      }, groupName, index)
      if (item) result.push(item)
    })
  }
  return result
}

function latestTransactionByPump(items) {
  const result = new Map()
  for (const item of items ?? []) {
    const data = item?.data ?? item
    const id = firstNumber(data?.Pump, data?.PumpId)
    if (id != null && !result.has(id)) result.set(id, data)
  }
  return result
}

function projectPumps(status, transactions) {
  const latest = latestTransactionByPump(transactions)
  const pumps = statusPumps(status)
  return pumps.sort((left, right) => left.id - right.id).map((pump) => {
    const transaction = latest.get(pump.id)
    return {
      ...pump,
      price: pump.price ?? firstNumber(transaction?.Price),
      fuelGradeName: pump.fuelGradeName ?? textOrNull(transaction?.FuelGradeName ?? transaction?.ProductName),
      volume: pump.volume ?? (pump.state === 'filling' ? firstNumber(transaction?.Volume) : null),
      amount: pump.amount ?? (pump.state === 'filling' ? firstNumber(transaction?.Amount) : null),
    }
  })
}

function tankWarning(data, fillPercent) {
  const status = String(data?.Status ?? '').toUpperCase()
  const alarms = Array.isArray(data?.Alarms) ? data.Alarms.join(' ').toUpperCase() : String(data?.Alarms ?? '').toUpperCase()
  if (status.includes('LOW') || alarms.includes('LOW') || (fillPercent != null && fillPercent <= 35)) return 'Низький рівень'
  return null
}

function normalizeTank(item) {
  const data = item?.data ?? item
  const id = firstNumber(data?.Tank, data?.TankId, data?.Number)
  if (id == null) return null
  const volume = firstNumber(data?.ProductVolume, data?.Volume)
  const freeVolume = firstNumber(data?.Ullage, data?.FreeVolume)
  const capacity = firstNumber(data?.Capacity, data?.TankCapacity,
    volume != null && freeVolume != null ? volume + freeVolume : null)
  const fillPercent = clampPercent(data?.TankFillingPercentage ?? data?.FillPercent
    ?? (volume != null && capacity ? volume / capacity * 100 : null))
  return {
    id,
    productName: textOrNull(data?.FuelGradeName ?? data?.ProductName ?? data?.Product),
    volume,
    freeVolume,
    fillPercent,
    warning: tankWarning(data, fillPercent),
  }
}

function projectTanks(status, measurements) {
  const source = [...(measurements ?? [])]
  const current = status?.Probes?.Measurements ?? status?.Tanks?.Measurements ?? status?.Tanks
  if (Array.isArray(current)) source.push(...current)
  const tanks = new Map()
  for (const item of source) {
    const tank = normalizeTank(item)
    if (tank && !tanks.has(tank.id)) tanks.set(tank.id, tank)
  }
  return [...tanks.values()].sort((left, right) => left.id - right.id)
}

function alertSeverity(data) {
  const value = `${data?.Severity ?? ''} ${data?.Name ?? ''} ${data?.Code ?? ''}`.toUpperCase()
  if (/CRITICAL|OFFLINE|NO.?CONNECTION|DISCONNECT|READER|ЗВ.?ЯЗ/.test(value)) return 'critical'
  if (/INFO|INFORMATION/.test(value)) return 'info'
  return 'warning'
}

function alertDeviceLabel(data, translate) {
  const type = String(data?.DeviceType ?? '').toUpperCase()
  const number = firstNumber(data?.DeviceNumber, data?.Tank, data?.Pump)
  const suffix = number == null ? '' : ` №${number}`
  const label = (key, fallback) => translate ? translate(key) : fallback
  if (type.includes('TANK')) return `${label('pts2UiTank', 'Резервуар')}${suffix}`
  if (type.includes('READER')) return `${label('pts2UiReader', 'Зчитувач')}${suffix}`
  if (type.includes('PUMP')) return `${label('pts2UiPump', 'ПРК')}${suffix}`
  return type === 'PTS' ? label('pts2UiController', 'Контролер') : (textOrNull(data?.DeviceType) ?? label('pts2UiEquipment', 'Обладнання'))
}

function alertMessage(data, translate) {
  const name = String(data?.Name ?? '').toUpperCase()
  const label = (key, fallback) => translate ? translate(key) : fallback
  if (name.includes('LOWLEVEL') || name.includes('LOW_LEVEL')) return label('pts2UiAlertLowLevel', 'низький рівень')
  if (name.includes('LOWBATTERY') || name.includes('LOW_BATTERY')) return label('pts2UiAlertLowBattery', 'низький заряд батареї')
  if (name.includes('OFFLINE') || name.includes('DISCONNECT') || name.includes('CONNECTION')) return label('pts2UiAlertOffline', 'немає зв’язку')
  return textOrNull(data?.Message ?? data?.Name) ?? (translate ? translate('pts2UiAlertCode', { code: data?.Code ?? '' }) : `тривога ${data?.Code ?? ''}`.trim())
}

function normalizeAlert(item, translate) {
  const data = item?.data ?? item
  if (terminalAlertStates.has(String(data?.State ?? '').toUpperCase())) return null
  return {
    id: item?.id ?? `${data?.DeviceType ?? 'device'}-${data?.DeviceNumber ?? 0}-${data?.Code ?? data?.Name ?? 'alert'}`,
    title: `${alertDeviceLabel(data, translate)} — ${alertMessage(data, translate)}`,
    eventAt: item?.eventAt ?? data?.DateTime ?? null,
    severity: alertSeverity(data),
  }
}

function projectAlerts(status, alerts, translate) {
  const source = [...(alerts ?? [])]
  if (Array.isArray(status?.Alerts)) source.push(...status.Alerts)
  const result = []
  const seen = new Set()
  for (const item of source) {
    const alert = normalizeAlert(item, translate)
    if (!alert) continue
    const key = `${alert.title}|${alert.eventAt ?? ''}`
    if (seen.has(key)) continue
    seen.add(key)
    result.push(alert)
  }
  return result.slice(0, 8)
}

function projectHealth(status, controller) {
  const usedBytes = firstNumber(status?.Storage?.UsedBytes, status?.SdUsedBytes)
  const totalBytes = firstNumber(status?.Storage?.TotalBytes, status?.SdTotalBytes)
  return {
    firmwareDateTime: status?.FirmwareDateTime ?? controller?.firmwareDateTime ?? null,
    firmwareVersion: textOrNull(status?.FirmwareVersion ?? status?.Version),
    cpuTemperature: firstNumber(status?.CpuTemperature),
    batteryVoltage: (() => {
      const value = firstNumber(status?.BatteryVoltage)
      return value != null && value > 20 ? value / 1000 : value
    })(),
    sdMounted: typeof status?.SdMounted === 'boolean' ? status.SdMounted : null,
    storage: { usedBytes, totalBytes },
    uptimeSeconds: firstNumber(status?.PtsStartupSeconds),
  }
}

export function projectPts2Dashboard({ controller = null, snapshot = null, transactions = [], measurements = [], alerts = [], translate } = {}) {
  const status = snapshot?.status ?? {}
  const freshness = pts2FreshnessView(snapshot?.freshness ?? controller?.freshness ?? controller?.connectionState)
  return {
    controller: {
      id: controller?.id ?? snapshot?.controllerId ?? null,
      code: textOrNull(controller?.stableCode ?? controller?.displayName),
      name: textOrNull(controller?.displayName),
      freshness,
      receivedAt: snapshot?.receivedAt ?? controller?.lastSeenAt ?? null,
      transport: transportLabel(snapshot?.transport ?? snapshot?.source),
    },
    pumps: projectPumps(status, transactions),
    tanks: projectTanks(status, measurements),
    alerts: projectAlerts(status, alerts, translate),
    health: projectHealth(status, controller),
  }
}

export function usePts2CurrentStatus({ pollInterval = 3000 } = {}) {
  const { t } = useI18n()
  const snapshot = ref(null)
  const transactions = ref([])
  const measurements = ref([])
  const alerts = ref([])
  const loading = ref(true)
  const switching = ref(false)
  const refreshing = ref(false)
  const error = ref('')
  const noCurrentData = ref(false)
  const announcement = ref('')
  const requiresExplicitSelection = ref(false)
  let timer = null
  let requestNumber = 0
  let revalidatingSequence = null
  let recoveringHistoriesSequence = null

  const context = computed(() => {
    const value = normalizePts2Context(session.pts2Context ?? {})
    return requiresExplicitSelection.value
      ? { ...value, activeControllerId: null, selectionRequired: value.controllers.length > 0 }
      : value
  })
  const activeController = computed(() => context.value.controllers.find(
    (item) => Number(item.id) === Number(context.value.activeControllerId),
  ) ?? null)
  const dashboard = computed(() => projectPts2Dashboard({
    controller: activeController.value,
    snapshot: snapshot.value,
    transactions: transactions.value,
    measurements: measurements.value,
    alerts: alerts.value,
    translate: t,
  }))

  function clearCurrentData() {
    snapshot.value = null
    transactions.value = []
    measurements.value = []
    alerts.value = []
    noCurrentData.value = false
  }

  function currentSourceScope(controllerId) {
    const owner = () => JSON.stringify([
      session.user?.id ?? session.user?.username, session.user?.accessVersion,
      session.platformContext?.activeStationId, context.value.stationId,
    ])
    const expectedOwner = owner()
    return () => Boolean(session.user) && owner() === expectedOwner
      && context.value.activeControllerId === controllerId
  }

  function clearDeniedSource(cause, controllerId) {
    if (![401, 403, 404].includes(cause?.status)) return false
    requestNumber++
    clearCurrentData()
    loading.value = false
    requiresExplicitSelection.value = true
    if (session.pts2Context) session.pts2Context = {
      ...session.pts2Context, activeControllerId: null, selectionRequired: true,
      controllers: (session.pts2Context.controllers ?? []).filter((item) => Number(item.id) !== Number(controllerId)),
    }
    return true
  }

  async function revalidateMissingStatus(controllerId, isCurrentSource) {
    const sequence = ++requestNumber
    revalidatingSequence = sequence
    clearCurrentData()
    try {
      // GET status uses the same opaque 404 for denied sources and an absent snapshot.
      // Recheck the catalog before committing shared context, including after unmount.
      const trustedContext = await getPts2Context()
      if (sequence !== requestNumber || !isCurrentSource()) return
      const sameStation = Number(trustedContext?.stationId) === context.value.stationId
        && Number(trustedContext?.stationId) === Number(session.platformContext?.activeStationId)
      const stillGranted = sameStation && Number(trustedContext?.activeControllerId) === controllerId
        && (trustedContext?.controllers ?? []).some((item) => Number(item.id) === controllerId)
      if (sameStation) session.pts2Context = trustedContext
      if (stillGranted) {
        requiresExplicitSelection.value = false
        noCurrentData.value = true
        error.value = ''
      } else {
        clearDeniedSource({ status: 404 }, controllerId)
        error.value = t('pts2UiControllerDenied')
      }
    } catch (cause) {
      if (sequence !== requestNumber || !isCurrentSource()) return
      // An unverified catalog cannot establish access after an ambiguous status response.
      clearDeniedSource({ status: 404 }, controllerId)
      error.value = t([401, 403, 404].includes(cause?.status) ? 'pts2UiControllerDenied' : 'pts2UiContextError')
    } finally {
      if (sequence === requestNumber) loading.value = false
      if (revalidatingSequence === sequence) revalidatingSequence = null
    }
  }

  async function loadHistories(controllerId, sequence, isCurrentSource) {
    const flows = ['transactions', 'tank-measurements', 'alerts']
    const results = await Promise.allSettled(flows.map((flow) => getPts2History(flow, controllerId, { limit: 100 })))
    if (sequence !== requestNumber || !isCurrentSource()) return
    transactions.value = results[0].status === 'fulfilled' ? (results[0].value.items ?? []) : []
    measurements.value = results[1].status === 'fulfilled' ? (results[1].value.items ?? []) : []
    alerts.value = results[2].status === 'fulfilled' ? (results[2].value.items ?? []) : []
  }

  async function loadCurrent({ includeHistories = false, quiet = false } = {}) {
    const controllerId = context.value.activeControllerId
    if (!controllerId) {
      clearCurrentData()
      loading.value = false
      return
    }
    const sequence = ++requestNumber
    const isCurrentSource = currentSourceScope(controllerId)
    const recoverHistories = noCurrentData.value
    if (!quiet) loading.value = true
    error.value = ''
    try {
      const currentRequest = getPts2CurrentStatus(controllerId)
      const historyRequest = includeHistories ? loadHistories(controllerId, sequence, isCurrentSource) : Promise.resolve()
      const current = await currentRequest
      if (sequence !== requestNumber || !isCurrentSource()) return
      snapshot.value = current
      noCurrentData.value = false
      if (recoverHistories && !includeHistories) {
        recoveringHistoriesSequence = sequence
        await loadHistories(controllerId, sequence, isCurrentSource)
      }
      await historyRequest
    } catch (cause) {
      if (sequence === requestNumber && isCurrentSource()) {
        if (cause?.status === 404) await revalidateMissingStatus(controllerId, isCurrentSource)
        else error.value = t(clearDeniedSource(cause, controllerId) ? 'pts2UiControllerDenied' : 'pts2UiStatusError')
      }
    } finally {
      if (sequence === requestNumber) loading.value = false
      if (recoveringHistoriesSequence === sequence) recoveringHistoriesSequence = null
    }
  }

  async function initialize() {
    loading.value = true
    error.value = ''
    try {
      session.pts2Context = normalizePts2Context(await loadPts2Context({ force: true }))
      await loadCurrent({ includeHistories: true })
    } catch (cause) {
      error.value = t('pts2UiContextError')
      loading.value = false
    }
  }

  async function selectController(controllerId) {
    if (!controllerId || Number(controllerId) === context.value.activeControllerId) return
    switching.value = true
    requestNumber++
    refreshing.value = false
    error.value = ''
    clearCurrentData()
    try {
      session.pts2Context = normalizePts2Context(await savePts2Controller(Number(controllerId)))
      requiresExplicitSelection.value = false
      await loadCurrent({ includeHistories: true })
      announcement.value = t('pts2UiControllerSelected', { name: activeController.value?.stableCode ?? activeController.value?.displayName ?? controllerId })
    } catch (cause) {
      error.value = t(clearDeniedSource(cause, controllerId) ? 'pts2UiControllerDenied' : 'pts2UiSelectionError')
    } finally {
      switching.value = false
    }
  }

  async function refresh() {
    const controllerId = context.value.activeControllerId
    if (!controllerId) return
    refreshing.value = true
    error.value = ''
    try {
      await requestPts2CurrentStatusRefresh(controllerId)
      await loadCurrent({ includeHistories: true, quiet: true })
      announcement.value = t('pts2UiRefreshAccepted')
    } catch (cause) {
      if (!switching.value && context.value.activeControllerId === controllerId) {
        error.value = t(clearDeniedSource(cause, controllerId) ? 'pts2UiControllerDenied' : 'pts2UiRefreshError')
      }
    } finally {
      refreshing.value = false
    }
  }

  function startPolling() {
    if (timer) return
    timer = window.setInterval(() => {
      if (!document.hidden && context.value.activeControllerId && !loading.value && !refreshing.value
        && revalidatingSequence !== requestNumber && recoveringHistoriesSequence !== requestNumber) {
        loadCurrent({ quiet: true })
      }
    }, pollInterval)
  }

  function stopPolling() {
    if (timer) window.clearInterval(timer)
    timer = null
    requestNumber += 1
  }

  onMounted(async () => {
    await initialize()
    startPolling()
  })
  onBeforeUnmount(stopPolling)

  return {
    context,
    activeController,
    dashboard,
    loading,
    switching,
    refreshing,
    error,
    noCurrentData,
    announcement,
    initialize,
    loadCurrent,
    refresh,
    selectController,
  }
}
